// import { setupCommonRouteHijacks } from '@m2h/common/utils/iframeLocationHijack';
import { isWebAlert } from '@m2h/common/constants';
import { MACHPRO_ALERT_CLOSE } from '@m2h/common/constants/postMsg';
import { sendParent } from '@m2h/common/utils/postMsg';

const isTop = window.self === window.top;

const sandbox = [
  'allow-scripts',
  'allow-same-origin',
  // 'allow-forms',
  'allow-top-navigation-to-custom-protocols',
  'allow-top-navigation-by-user-activation',
  'allow-top-navigation',
  'allow-popups',
];

export interface AlertIframeCloseOptions {
  fromPopState?: boolean;
}

export interface AlertIframeOpenParams {
  url: string;
  onload?: (e: Event) => void;
}

const ALERT_HISTORY_ID = '__machproAlertHistoryId';

export const sendParentCloseIframe = () => {
  sendParent(MACHPRO_ALERT_CLOSE);
};

class AlertIframeManager {
  private iframe: HTMLIFrameElement | null = null;
  public isOpen = false;
  private alertHistoryId: string | null = null;
  private pendingHistoryBackId: string | null = null;
  private queuedOpenParams: AlertIframeOpenParams | null = null;
  private eventListeners: Array<{ target: any; event: string; handler: Function; }> = [];
  constructor() {
    this.iframe = this.createIframeElement();
    this.isOpen = false;
  }
  public createIframeElement() {
    const dom = document.createElement('iframe');
    dom.id = 'root_alert_iframe';
    // 添加 sandbox 属性以限制 iframe 的能力
    // dom.setAttribute('sandbox', sandbox.join(' '));
    // 添加 CSP 以阻止 Google 地图等外部资源的加载(可选,如果需要完全阻止)
    // dom.setAttribute('csp', "default-src 'self'; connect-src 'self'");
    // this.iframe = dom;
    // document.body.appendChild(this.iframe);
    // 以上提前加载iframe会有蒙层关闭问题
    // 初始化iframe location劫持
    // setupCommonRouteHijacks();
    return dom;
  }
  public open(params: AlertIframeOpenParams) {
    if (!isTop) return;
    console.log('open', this.isOpen);
    if (this.pendingHistoryBackId) {
      // 主动关闭触发的 history.back() 尚未落地时，只保留最新打开请求。
      // 待对应 popstate 清理旧 owner 后再创建，避免旧回调误关新弹层。
      this.queuedOpenParams = params;
      return;
    }
    if (this.isOpen && this.iframe) {
      // 已有弹层只更新内容，不再叠加 iframe/history，避免连续点击产生无法解释的返回层级。
      this.eventListeners.forEach(({ target, event, handler }) => {
        try {
          target.removeEventListener(event, handler as EventListener);
        } catch (e) {
          console.warn(`Failed to remove event listener: ${event}`, e);
        }
      });
      this.eventListeners = [];
      this.iframe.src = params.url;
      this.iframe.classList.add('show');
      this.iframe.onload = (e: Event) => {
        params.onload && params.onload(e);
      };
      return;
    }
    if (!this.iframe) {
      this.iframe = this.createIframeElement();
    }
    document.body.appendChild(this.iframe);

    // 初始化iframe location劫持
    // setupCommonRouteHijacks();

    this.isOpen = true;
    this.iframe.src = params.url;
    this.iframe?.classList.add('show');

    // 保存 onload 回调的包装函数,以便后续清理
    const onloadWrapper = (e: Event) => {
      params.onload && params.onload(e);
    };
    this.iframe.onload = onloadWrapper;

    // 添加 click 事件监听器并记录引用
    if (this.iframe?.contentWindow?.document.body) {
      this.iframe.contentWindow.document.body.addEventListener('click', sendParentCloseIframe);
      this.eventListeners.push({
        target: this.iframe.contentWindow.document.body,
        event: 'click',
        handler: sendParentCloseIframe
      });
    }

    const alertHistoryId = `alert_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    this.alertHistoryId = alertHistoryId;
    history.pushState(
      {
        ...(history.state || {}),
        [ALERT_HISTORY_ID]: alertHistoryId,
      },
      '',
      location.href
    );
  }
  // public close() {
  //   if (!isTop || !this.isOpen || !this.iframe) return;
  //   this.isOpen = false;
  //   document.body.removeChild(this.iframe);
  //   this.iframe.src = '';
  //   this.iframe.className = '';
  // }
  public close(options: AlertIframeCloseOptions = {}) {
    console.log('closeAlertIframe iframe close');
    if (!isTop || !this.isOpen || !this.iframe) return;
    this.isOpen = false;

    const alertHistoryId = this.alertHistoryId;
    this.alertHistoryId = null;

    let iframeToClean: HTMLIFrameElement | null = this.iframe;

    // 立即隐藏 iframe
    iframeToClean.style.display = 'none';
    iframeToClean.className = '';

    // 移除 onload/onerror 事件监听
    iframeToClean.onload = null;
    iframeToClean.onerror = null;

    // 第一步: 立即设置 src 为 about:blank,这会中断所有网络请求
    iframeToClean.src = 'about:blank';

    try {
      // 移除所有记录的事件监听器
      this.eventListeners.forEach(({ target, event, handler }) => {
        try {
          target.removeEventListener(event, handler as EventListener);
        } catch (e) {
          console.warn(`Failed to remove event listener: ${event}`, e);
        }
      });
      this.eventListeners = [];

      // 彻底清理 iframe 内容和资源
      if (iframeToClean.contentWindow) {
        try {
          // 1. 停止所有正在进行的网络请求
          iframeToClean.contentWindow.stop?.();

          // 2. 尝试清理 Google 地图相关的全局对象
          if ((iframeToClean.contentWindow as any).google?.maps) {
            // 清空 Google 地图实例
            delete (iframeToClean.contentWindow as any).google.maps;
          }

          // 3. 立即替换为 about:blank,中断所有活动
          // iframeToClean.contentWindow.location.replace('about:blank');
          // delete 发生在 realm 还活着时，等于主动制造“全局变量消失但回调还活着”的窗口期，不应该
          // delete iframeToClean.contentWindow.Mach;
        } catch (e) {
          console.warn('Failed to stop iframe window:', e);
        }
      }
    } catch (e) {
      console.log('closeAlertIframe iframe error');
      // 跨域情况下可能会报错,忽略
      console.warn('Failed to remove iframe event listeners:', e);
    } finally {
      // 最后移除 Mach 监听器,确保所有清理完成
      Mach.off('close', sendParentCloseIframe);
    }

    if (iframeToClean) {
      // 从 DOM 中移除
      if (iframeToClean.parentNode) {
        document.body.removeChild(iframeToClean);
      }

      // 清空所有属性
      iframeToClean.removeAttribute('src');
      iframeToClean.removeAttribute('id');
      iframeToClean.removeAttribute('class');
      iframeToClean.removeAttribute('style');

      // 立即清空引用
      this.iframe = null;
      iframeToClean = null;
    }

    // 强制垃圾回收提示(仅在支持的浏览器中)
    if ((window as any).gc) {
      try {
        (window as any).gc();
      } catch (e) {
        // 忽略错误
      }
    }

    // 主动关闭需要对称消费 open() 压入的当前 history；浏览器返回触发的关闭已经消费过该条目。
    if (!options?.fromPopState && alertHistoryId
      && history.state?.[ALERT_HISTORY_ID] === alertHistoryId) {
      this.pendingHistoryBackId = alertHistoryId;
      history.back();
    }
  }

  public handlePopState() {
    if (!this.pendingHistoryBackId) return false;

    this.pendingHistoryBackId = null;
    const queuedOpenParams = this.queuedOpenParams;
    this.queuedOpenParams = null;
    if (queuedOpenParams) {
      this.open(queuedOpenParams);
    }
    return true;
  }
}

if (!window.alertIframeManager && !isWebAlert) {
  window.alertIframeManager = new AlertIframeManager();
  // // 初始化iframe location劫持
  // setupCommonRouteHijacks();
}

export const getAlertIframe = () => document.getElementById('root_alert_iframe') as HTMLIFrameElement | null;

export const openAlertIframe = (params: AlertIframeOpenParams) => {
  window.alertIframeManager.open(params);
};

export const closeAlertIframe = (options?: AlertIframeCloseOptions) => {
  window.alertIframeManager.close(options);
};

export const isAlertIframeOpen = () => window.alertIframeManager.isOpen;

export const handleAlertIframePopState = () => (
  window.alertIframeManager.handlePopState?.() || false
);
